import Editor from '@monaco-editor/react';

interface Props {
  executionMode: string;
  sqlSetup: string;
  sqlReferenceQuery: string;
  orderMatters: boolean;
  onChange: (
    field: 'execution_mode' | 'sql_setup' | 'sql_reference_query' | 'order_matters',
    value: string | boolean
  ) => void;
}

export function ProblemSqlTab({
  executionMode,
  sqlSetup,
  sqlReferenceQuery,
  orderMatters,
  onChange,
}: Props) {
  const isSql = executionMode === 'sql';
  // A function-mode problem can't be represented by this boolean toggle. Leaving
  // it enabled would let an unchecked box write execution_mode: 'stdio' on save,
  // silently converting the problem to stdio while its signature stays populated.
  const isFunctionMode = executionMode === 'function';

  return (
    <div className="flex flex-col gap-4 p-4">
      <label
        className={`flex items-center gap-2.5 text-sm font-medium text-foreground ${
          isFunctionMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          checked={isSql}
          disabled={isFunctionMode}
          onChange={(e) => onChange('execution_mode', e.target.checked ? 'sql' : 'stdio')}
          className="h-4 w-4 rounded border-[#ececec] accent-orange-500 disabled:cursor-not-allowed"
        />
        This is a SQL problem
      </label>

      {isFunctionMode && (
        <p className="text-[12px] text-muted-foreground">
          This problem uses function-mode (it has a signature) — SQL mode can&apos;t be
          combined with function-mode, so this toggle is locked.
        </p>
      )}

      {isSql && (
        <>
          <div className="flex flex-col gap-2">
            <div className="text-[12px] font-semibold text-foreground">
              Schema Setup (DDL + seed data)
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Editor
                height="220px"
                language="pgsql"
                value={sqlSetup}
                onChange={(val) => onChange('sql_setup', val ?? '')}
                theme="vs-dark"
                options={{
                  fontSize: 12,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-[12px] font-semibold text-foreground">
              Reference Query (the golden answer)
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Editor
                height="160px"
                language="pgsql"
                value={sqlReferenceQuery}
                onChange={(val) => onChange('sql_reference_query', val ?? '')}
                theme="vs-dark"
                options={{
                  fontSize: 12,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                }}
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
            <input
              type="checkbox"
              checked={orderMatters}
              onChange={(e) => onChange('order_matters', e.target.checked)}
              className="h-4 w-4 rounded border-[#ececec] accent-orange-500"
            />
            Row order matters for grading (e.g. this problem tests ORDER BY)
          </label>
        </>
      )}
    </div>
  );
}
